import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC } from 'react';
import { Link, useParams } from 'react-router-dom';

import DetailsPanel from 'components/DetailsPanel';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';

import alIndiaLogo from 'media/alIndiaLogo.png';

import unitTransferReasonToReadable from 'utils/enums/unit-transfer-reason-to-readable';
import unitTransferStatusToReadable from 'utils/enums/unit-transfer-status-to-readable';

const UNIT_TRANSFER_REQUEST_BY_TOKEN_QUERY = gql(`
  query UnitTransferRequestByToken($token: String!) {
    unitTransferRequestByToken(token: $token) {
      id
      transferorName
      transfereeName
      reason
      schemeCount
      totalUnits
      status
      hasResponded
    }
  }
`);

const StatusBadge: FC<{ value: any }> = ({ value }) => {
  const { label, color } = unitTransferStatusToReadable(value);
  return <Badge label={label} color={color} showIndicator />;
};

const UnitTransferPreview: FC = () => {
  const { token } = useParams<{ token: string }>() as { token: string };

  const { data, loading, error } = useQuery(UNIT_TRANSFER_REQUEST_BY_TOKEN_QUERY, {
    variables: { token },
  });

  const request = data?.unitTransferRequestByToken;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <img className="h-10 w-auto" src={alIndiaLogo} alt="AngelList India" />
        </div>
      </header>

      <main className="flex flex-1 flex-col items-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {loading ? (
            <div className="rounded-lg bg-white p-8 shadow">
              <LoadingIndicator />
            </div>
          ) : null}

          {!loading && (error || !request) ? (
            <div className="rounded-lg bg-white p-8 shadow text-center">
              <h2 className="text-xl font-semibold text-gray-900">Link not found</h2>
              <p className="mt-2 text-sm text-gray-600">
                This unit transfer preview link is invalid or has expired.
              </p>
            </div>
          ) : null}

          {!loading && request ? (
            <div className="space-y-4">
              <DetailsPanel
                title="Unit transfer request"
                subTitle={`${request.transferorName} wants to transfer units to ${request.transfereeName}.`}
                displayHorizontally
                data={[
                  { label: 'From', value: request.transferorName },
                  { label: 'To', value: request.transfereeName },
                  { label: 'Reason', value: unitTransferReasonToReadable(request.reason) },
                  { label: 'Schemes', value: request.schemeCount, type: 'NUMBER' },
                  { label: 'Total units', value: request.totalUnits, type: 'NUMBER' },
                  {
                    label: 'Status',
                    value: request.status,
                    type: 'CUSTOM',
                    customValue: StatusBadge,
                  },
                ]}
              />

              <p className="text-center text-sm text-gray-600">
                {request.hasResponded
                  ? 'You have already responded to this request.'
                  : 'Log in to accept or decline this request.'}
              </p>

              <Link to={`/invest/unit-transfers/${request.id}/acknowledge`}>
                <Button className="w-full">Log in to respond</Button>
              </Link>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default UnitTransferPreview;
